import { pickBroadcastRecruitSurvivor } from './broadcastRecruitPool'
import { getSkillLevel } from './skills'
import type { Survivor, WorkstationRuntimeState } from './types'

/** Pending recruit slots cap by workstation level (plan: L1→1, L2+→2). */
export function maxBroadcastPendingOffers(level: number): number {
  const lv = Math.max(0, Math.floor(level))
  if (lv <= 0) return 0
  return Math.min(lv, 2)
}

const LEVEL_MS: Record<number, number> = {
  1: 26 * 60 * 1000,
  2: 22 * 60 * 1000,
  3: 18 * 60 * 1000,
}

export function broadcastCooldownMs(
  level: number,
  assignedSurvivor: Survivor | undefined,
): number | null {
  const lv = Math.max(1, Math.floor(level))
  const base = LEVEL_MS[lv] ?? LEVEL_MS[3]!
  if (!assignedSurvivor) return base
  const electronics = getSkillLevel(assignedSurvivor, 'electronics')
  const linguistics = getSkillLevel(assignedSurvivor, 'linguistics')
  const survival = getSkillLevel(assignedSurvivor, 'survival')
  const skillBlend = electronics * 0.35 + linguistics * 0.35 + survival * 0.3
  const reduction = skillBlend * 0.028
  const multiplier = Math.max(0.55, 1 - reduction)
  return Math.max(60_000, Math.round(base * multiplier))
}

export function resolveBroadcastRecruitmentStep(
  workstations: Record<string, WorkstationRuntimeState>,
  survivors: Survivor[],
  now: number,
): {
  workstations: Record<string, WorkstationRuntimeState>
  changed: boolean
} {
  const ws = workstations.broadcast_station
  if (!ws?.built || ws.level < 1 || !ws.assignedSurvivorId) {
    return { workstations, changed: false }
  }

  const maxOffers = maxBroadcastPendingOffers(ws.level)
  let offers = [...ws.broadcastRecruitOffers]
  if (offers.length >= maxOffers) {
    return { workstations, changed: false }
  }

  const assigned = survivors.find((s) => s.id === ws.assignedSurvivorId)
  const cooldown = broadcastCooldownMs(ws.level, assigned)
  if (!cooldown) return { workstations, changed: false }

  let anchor = ws.generationAnchorAt ?? now
  if (anchor > now) return { workstations, changed: false }

  let elapsed = now - anchor
  if (elapsed < cooldown) {
    if (ws.generationAnchorAt == null) {
      return {
        workstations: {
          ...workstations,
          broadcast_station: { ...ws, generationAnchorAt: now },
        },
        changed: true,
      }
    }
    return { workstations, changed: false }
  }

  let changed = false
  while (elapsed >= cooldown && offers.length < maxOffers) {
    const next = pickBroadcastRecruitSurvivor(survivors, offers)
    if (!next) {
      anchor += cooldown
      elapsed -= cooldown
      changed = true
      break
    }
    offers.push(next)
    elapsed -= cooldown
    anchor += cooldown
    changed = true
  }

  if (!changed) return { workstations, changed: false }

  return {
    workstations: {
      ...workstations,
      broadcast_station: {
        ...ws,
        broadcastRecruitOffers: offers,
        generationAnchorAt: anchor,
      },
    },
    changed: true,
  }
}
